import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MobileSigninPage = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: enter mobile, 2: enter OTP
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { requestOtp, verifyOtp } = useAuth();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!/^\d{10}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number.');
      setLoading(false);
      return;
    }

    try {
      const payload = { mobile, purpose: 'signin' };
      const res = await requestOtp(payload);
      setStep(2);
      setLoading(false);
      const retryAfter = (res && res.retry_after) || 30;
      setResendTimer(Number(retryAfter) || 30);
      setResendInfo(`OTP sent to ${mobile}`);
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const retryAfter = err?.response?.data?.retry_after || err?.response?.data?.retryAfter;
      if (status === 429 && retryAfter) {
        setResendTimer(Number(retryAfter) || 30);
        setError('Too many requests. Try again later.');
      } else if (status === 400) {
        if (errorCode === 'MOBILE_NOT_FOUND') {
          setError('No account found for this mobile. Please sign up to continue.');
        } else if (errorCode === 'MOBILE_INACTIVE') {
          setError('Your account is inactive. Please contact support to activate your account.');
        } else {
          setError(err?.response?.data?.message || 'Invalid request.');
        }
      } else {
        if (status === 500 || errorCode === 'SMS_SEND_FAILED') {
          setError('Internal Server Error. Please try again later.');
        } else {
          setError('Internal Server Error. Please try again later.');
        }
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setLoading(true);
    setError('');
    if (!/^[0-9]{4,6}$/.test(otp)) {
      setError('Please enter a valid code.');
      setLoading(false);
      return;
    }

    try {
      const payload = { mobile, code: otp, purpose: 'signin' };
      const res = await verifyOtp(payload);
      setLoading(false);
      if (res?.access) {
        navigate('/home');
      } else {
        setError('Verification failed.');
      }
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      if (status === 400) {
        if (errorCode === 'INVALID_OR_EXPIRED_CODE') {
          setError('Invalid or expired code. Please re-enter or resend a new OTP.');
        } else if (errorCode === 'MOBILE_NOT_FOUND') {
          setError('No account found for this mobile. Please sign up to continue.');
        } else if (errorCode === 'MOBILE_INACTIVE') {
          setError('Your account is inactive. Please contact support to activate your account.');
        } else {
          setError(err?.response?.data?.message || 'Invalid code.');
        }
      } else {
        setError('Unable to verify OTP. Please try again later.');
      }
    }
  };

  const [isResending, setIsResending] = useState(false);
  const [resendInfo, setResendInfo] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const resendIntervalRef = useRef(null);

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsResending(true);
    setResendInfo('');
    setError('');
    try {
      const payload = { mobile, purpose: 'signin' };
      const res = await requestOtp(payload);
      setIsResending(false);
      setResendInfo('OTP resent. Please check your mobile.');
      const retryAfter = (res && res.retry_after) || 30;
      setResendTimer(Number(retryAfter) || 30);
    } catch (err) {
      setIsResending(false);
      const status = err?.response?.status;
      const errorCode = err?.response?.data?.error_code;
      const retryAfter = err?.response?.data?.retry_after || err?.response?.data?.retryAfter;
      if (status === 429 && retryAfter) {
        setResendTimer(Number(retryAfter) || 30);
        setError('Too many requests. Try again later.');
      } else if (status === 400) {
        if (errorCode === 'MOBILE_NOT_FOUND') {
          setError('No account found for this mobile. Please sign up to continue.');
        } else if (errorCode === 'MOBILE_INACTIVE') {
          setError('Your account is inactive. Please contact support to activate your account.');
        } else {
          setError(err?.response?.data?.message || 'Invalid request.');
        }
      } else {
        if (status === 500 || errorCode === 'SMS_SEND_FAILED') {
          setError('Internal Server Error. Please try again later.');
        } else {
          setError('Internal Server Error. Please try again later.');
        }
      }
    }
  };

  useEffect(() => {
    if (resendTimer <= 0) {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
      return;
    }

    if (!resendIntervalRef.current) {
      resendIntervalRef.current = setInterval(() => {
        setResendTimer((t) => {
          if (t <= 1) {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }

    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
    };
  }, [resendTimer]);
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white rounded shadow">
        <h2 className="mb-6 text-2xl font-bold text-center text-primary">Sign In</h2>
        {step === 1 && (
          <form onSubmit={handleSendOtp}>
            <label className="block mb-2 text-sm font-medium" htmlFor="mobile">Mobile Number<span className="text-error ml-1">*</span></label>
            <input
              id="mobile"
              name="mobile"
              type="tel"
              className="input-field w-full mb-4"
              value={mobile}
              onChange={e => setMobile(e.target.value)}
              placeholder="Enter mobile number"
              required
              pattern="[0-9]{10}"
              maxLength={10}
              inputMode="numeric"
            />
            {error && <div className="mb-2 text-red-500">{error}</div>}
            <button
              type="submit"
              className="btn-primary w-full h-12 text-base"
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-sm font-medium" htmlFor="otp">Enter OTP<span className="text-error ml-1">*</span></label>
              <input
                id="otp"
                name="otp"
                type="text"
                required
                className="input-field md:max-w-sm w-full"
                value={otp}
                onChange={e => setOtp(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                placeholder="Enter OTP"
              />
            </div>
            {/* Verify button first */}
            <button
              type="button"
              className="btn-primary w-full h-12 text-base"
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            {/* Resend button second */}
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isResending || resendTimer > 0}
              className={`btn-secondary w-full h-12 text-base mb-2 ${isResending || resendTimer > 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
              aria-disabled={isResending || resendTimer > 0}
            >
              {isResending ? 'Resending…' : resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
            </button>
            {/* Centered message after both buttons */}
            {(resendInfo || error) && (
              <div className={`mb-2 text-sm ${error ? 'text-red-500' : 'text-green-600'}`} style={{ textAlign: 'center' }}>
                {error || resendInfo}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSigninPage;
